import { message, Input, Row, Col, Card, DatePicker, Space, Select } from "antd";
import React, { useEffect, useState } from "react";
import API from "../../api";
import { getUserRole } from "../../utils/auth";
import CustomTable from "../../components/Table/CustomTable";
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const { Search } = Input;
const { Option } = Select;

const SalesProfit = () => {
    const [sales, setSalesProfit] = useState([]);
    const [loading, setLoading] = useState(false);
    const [summary, setSummary] = useState(false);
    const [stores, setStores] = useState([]); // To hold store options
    const [selectedStoreName, setSelectedStoreName] = useState(null); // Selected store filter (name)
    const [paginationInfo, setPaginationInfo] = useState({
        current: 1,
        pageSize: 20,
        total: 0,
    });
    const [filters, setFilters] = useState({
        fromDate: null,
        toDate: null,
        financialYear: null, // New: Financial year filter
    });

    // Get user data from localStorage
    const userData = JSON.parse(localStorage.getItem('userData')) || {};
    const defaultStoreId = userData.storeId || null;

    // Fetch store list
    const fetchStores = async () => {
        try {
            const response = await API.get('/stores'); // Expecting [{ _id, name }, ...]
            setStores(response.data);

            // Set default store name based on storeId from localStorage
            const defaultStore = response.data.find(store => store._id === defaultStoreId);
            setSelectedStoreName(defaultStore ? defaultStore.name : null);
        } catch (error) {
            console.error('Error fetching stores:', error);
            message.error('Failed to fetch stores.');
        }
    };

    // Generate financial years starting from 2024-2025 to current financial year (April-based)
    const generateFinancialYears = () => {
        const currentDate = new Date();
        const financialYearStart = new Date(currentDate.getFullYear(), 3, 1); // April 1st

        let currentFinancialYearStart, currentFinancialYearEnd;
        if (currentDate < financialYearStart) {
            currentFinancialYearStart = currentDate.getFullYear() - 1;
            currentFinancialYearEnd = currentDate.getFullYear();
        } else {
            currentFinancialYearStart = currentDate.getFullYear();
            currentFinancialYearEnd = currentDate.getFullYear() + 1;
        }

        const financialYears = [];
        let startYear = 2024; // Start from 2024
        let endYear = startYear + 1;

        while (startYear <= currentFinancialYearStart) {
            financialYears.push(`${startYear}-${endYear}`);
            startYear++;
            endYear++;
        }

        return financialYears; // Return in ascending order (oldest to newest)
    };

    const handleFilterChange = (key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
    };

    const getSalesProfit = async () => {
        try {
            setLoading(true);

            // Find storeId based on selectedStoreName
            const selectedStore = selectedStoreName ? stores.find(store => store.name === selectedStoreName) : null;
            const storeId = selectedStore ? selectedStore._id : null;

            // Prepare query parameters dynamically
            const params = {
                page: paginationInfo.current,
                limit: paginationInfo.pageSize,
                fromDate: filters.fromDate ? dayjs(filters.fromDate).format("YYYY-MM-DD") : undefined,
                toDate: filters.toDate ? dayjs(filters.toDate).format("YYYY-MM-DD") : undefined,
                storeId: storeId || undefined,
                financialYear: filters.financialYear || undefined, // New: Add financialYear to params
            };

            const response = await API.get(`/dashboard/profit`, { params });

            const { products, pagination, summary } = response?.data || {};
            setSalesProfit(products || []);
            setSummary(summary);
            setPaginationInfo((prev) => ({
                ...prev,
                total: pagination?.totalProducts || 0,
            }));
        } catch (err) {
            console.error(err);
            message.error("Failed to fetch sales profit data. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStores(); // Fetch store list on mount
    }, []);

    useEffect(() => {
        if (stores.length > 0) {
            getSalesProfit(); // Fetch data when stores are loaded or filters change
        }
    }, [paginationInfo.current, paginationInfo.pageSize, filters, selectedStoreName, stores]);

    const columns = [
        {
            title: "Index",
            key: "index",
            render: (_, __, index) => (paginationInfo.current - 1) * paginationInfo.pageSize + index + 1,
        },
        {
            title: "Product Name",
            dataIndex: "productName",
            key: "productName",
        },
        {
            title: "Avg Purchase Price",
            dataIndex: "avgPurchasePrice",
            key: "avgPurchasePrice",
            render: (avgPurchasePrice) => `₹ ${(new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(avgPurchasePrice ?? 0))}`,
        },
        {
            title: "Avg Sale Price",
            dataIndex: "avgSalePrice",
            key: "avgSalePrice",
            render: (avgSalePrice) => `₹ ${(new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(avgSalePrice ?? 0))}`,
        },
        {
            title: "Total Sold",
            dataIndex: "totalSold",
            key: "totalSold",
        },
        {
            title: "Total Purchased",
            dataIndex: "totalPurchased",
            key: "totalPurchased",
        },
        {
            title: "Total Revenue",
            dataIndex: "totalRevenue",
            key: "totalRevenue",
            render: (totalRevenue) => `₹ ${(new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(totalRevenue ?? 0))}`,
        },
        {
            title: "Total Cost",
            dataIndex: "totalCost",
            key: "totalCost",
            render: (totalCost) => `₹ ${(new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(totalCost ?? 0))}`,
        },
        {
            title: "Total Profit",
            dataIndex: "totalProfit",
            key: "totalProfit",
            render: (totalProfit) => {
                const profit = totalProfit ?? 0;
                return (
                    <span style={{ color: profit <= 0 ? "red" : "black", fontWeight: "bold" }}>
                        ₹ {(new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(profit))}
                    </span>
                );
            },
            sorter: (a, b) => (a.totalProfit || 0) - (b.totalProfit || 0),
        },
    ];

    return (
        <div style={{ padding: "20px" }}>
            <Row gutter={[16, 16]}>
                <Col span={8}>
                    <Card
                        title="Total Revenue"
                        bordered
                        style={{
                            background: "#cff4fc",
                            borderRadius: "10px",
                            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                            textAlign: "center",
                        }}
                    >
                        <span style={{ fontSize: "1.5rem", fontWeight: "bold", color: "#055160" }}>
                            ₹ {(new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(summary?.totalRevenue ?? 0))}
                        </span>
                    </Card>
                </Col>
                <Col span={8}>
                    <Card
                        title="Total Cost"
                        bordered
                        style={{
                            background: "#f8d7da",
                            borderRadius: "10px",
                            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                            textAlign: "center",
                        }}
                    >
                        <span style={{ fontSize: "1.5rem", fontWeight: "bold", color: "#721c24" }}>
                            ₹ {(new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(summary?.totalCost ?? 0))}
                        </span>
                    </Card>
                </Col>
                <Col span={8}>
                    <Card
                        title="Total Profit"
                        bordered
                        style={{
                            background: "#d1e7dd",
                            borderRadius: "10px",
                            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                            textAlign: "center",
                        }}
                    >
                        <span style={{ fontSize: "1.5rem", fontWeight: "bold", color: "#155724" }}>
                            ₹ {(new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(summary?.totalProfit ?? 0))}
                        </span>
                    </Card>
                </Col>
            </Row>

            <Space align="end" style={{ padding: '10px 20px', display: 'flex', justifyContent: 'space-between' }}>
                {/* Store Filter */}
                <Select
                    style={{ width: 200 }}
                    placeholder="Select a store"
                    value={selectedStoreName || 'all'}
                    onChange={(value) => setSelectedStoreName(value === 'all' ? null : value)}
                    loading={loading}
                    disabled={loading}
                >
                    <Option value="all">All Stores</Option>
                    {stores.map((store) => (
                        <Option key={store._id} value={store.name}>
                            {store.name}
                        </Option>
                    ))}
                </Select>



                <DatePicker.RangePicker
                    value={filters.fromDate && filters.toDate ? [dayjs(filters.fromDate), dayjs(filters.toDate)] : []}
                    onChange={(dates) => {
                        if (dates && dates.length === 2) {
                            handleFilterChange("fromDate", dates[0].format("YYYY-MM-DD"));
                            handleFilterChange("toDate", dates[1].format("YYYY-MM-DD"));
                        } else {
                            handleFilterChange("fromDate", null);
                            handleFilterChange("toDate", null);
                        }
                    }}
                    format="YYYY-MM-DD"
                    allowClear
                />
                <Select
                    style={{ width: 150 }}
                    placeholder="Select Financial Year"
                    value={filters.financialYear}
                    onChange={(value) => handleFilterChange("financialYear", value)}
                    allowClear
                >
                    {generateFinancialYears().map((year) => (
                        <Option key={year} value={year}>
                            {year}
                        </Option>
                    ))}
                </Select>
            </Space>

            <Col style={{ textAlign: 'right' }}>
                <div>
                    <span>
                        Total Sold Amount:
                        <span style={{ color: summary?.PaymentCollection?.totalAmount >= 0 ? 'green' : 'red' }}>
                            ₹ {(new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(summary?.PaymentCollection?.totalAmount ?? 0))}
                        </span>
                    </span>
                    <br />
                    <span>
                        Amount Collected:
                        <span style={{ color: summary?.PaymentCollection?.paidAmount >= 0 ? 'green' : 'red' }}>
                            ₹ {(new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(summary?.PaymentCollection?.paidAmount ?? 0))}
                        </span>
                    </span>
                    <br />
                    <span>
                        Amount Remaining:
                        <span style={{ color: summary?.PaymentCollection?.balanceAmount <= 0 ? 'green' : 'red' }}>
                            ₹ {(new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(summary?.PaymentCollection?.balanceAmount ?? 0))}
                        </span>
                    </span>
                </div>
            </Col>

            <CustomTable
                columns={columns}
                dataSource={sales}
                onPaginationChange={(pagination) =>
                    setPaginationInfo({ current: pagination.current, pageSize: pagination.pageSize })
                }
                loading={loading}
                total={paginationInfo.total}
            />
        </div>
    );
};

export default SalesProfit;